import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send, CheckCircle2, Loader2 } from "lucide-react";
import { Reveal } from "@/components/Motion";
import Seo from "@/components/Seo";
import { CONTACT } from "@/lib/site-data";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTERESTS = [
  { value: "consulting", label: "IT Consulting" },
  { value: "training", label: "Training & Certification" },
  { value: "managed", label: "Managed Services" },
  { value: "staffing", label: "Recruiting & Staffing" },
  { value: "general", label: "General Inquiry" },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", company: "", interest: "general", message: "",
    website: "", // honeypot — leave blank
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      toast.success("Thanks! We'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", company: "", interest: "general", message: "", website: "" });
    } catch (err) {
      console.error(err);
      setStatus("error");
      if (err?.response?.status === 429) {
        toast.error("Too many submissions. Please wait a minute and try again.");
      } else {
        toast.error("Something went wrong. Please try again or email us directly.");
      }
    }
  };

  return (
    <div data-testid="page-contact">
      <Seo
        title="Contact Cutting Edge Technologies | Houston TX & Washington DC Office"
        description="Reach Cutting Edge Technologies Consulting at +1 (202) 538-0104 or Info@cetcusa.com. HQ: 8700 Commerce Park Dr, Suite 238, Houston, TX 77036. Serving Richmond, Sugar Land, Fulshear, Washington DC."
        path="/contact"
      />
      {/* Hero */}
      <section className="relative bg-slate-950 text-white pt-36 pb-20 md:pt-44 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 grid-lines-dark opacity-60" />
        <div className="absolute -top-20 -right-20 w-[500px] h-[500px] bg-emerald-500/10 blur-3xl rounded-full" />
        <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Reveal><div className="eyebrow text-emerald-400">Contact</div></Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[1.02] max-w-5xl">
              Let's build what's next.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-lg md:text-xl text-slate-300 leading-relaxed">
              Tell us about your goals. A senior partner will respond within one business day.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Form + info */}
      <section className="py-20 md:py-28 bg-white">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-14">
          {/* Info column */}
          <aside className="lg:col-span-4">
            <Reveal>
              <div className="eyebrow text-emerald-600">Reach us directly</div>
              <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight leading-tight">
                Three easy ways to connect.
              </h2>
            </Reveal>

            <div className="mt-10 space-y-6">
              <a href={CONTACT.phoneHref} className="group flex items-start gap-5 p-6 border border-slate-200 hover:border-slate-900 transition-colors" data-testid="contact-phone-card">
                <div className="h-12 w-12 bg-slate-900 text-emerald-400 flex items-center justify-center shrink-0 group-hover:bg-emerald-500 group-hover:text-slate-950 transition-colors">
                  <Phone className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="eyebrow text-slate-500">Phone</div>
                  <div className="mt-1 font-display text-lg text-slate-900">{CONTACT.phone}</div>
                  <div className="mt-1 text-sm text-slate-500">Mon–Fri, 9:00 AM – 6:00 PM CT</div>
                </div>
              </a>

              <a href={CONTACT.emailHref} className="group flex items-start gap-5 p-6 border border-slate-200 hover:border-slate-900 transition-colors" data-testid="contact-email-card">
                <div className="h-12 w-12 bg-slate-900 text-emerald-400 flex items-center justify-center shrink-0 group-hover:bg-emerald-500 group-hover:text-slate-950 transition-colors">
                  <Mail className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="eyebrow text-slate-500">Email</div>
                  <div className="mt-1 font-display text-lg text-slate-900">{CONTACT.email}</div>
                  <div className="mt-1 text-sm text-slate-500">Replies within 24 hours</div>
                </div>
              </a>

              <div className="flex items-start gap-5 p-6 border border-slate-200" data-testid="contact-address-card">
                <div className="h-12 w-12 bg-slate-900 text-emerald-400 flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="eyebrow text-slate-500">Headquarters</div>
                  <address className="mt-1 not-italic text-slate-900 leading-relaxed">
                    8700 Commerce Park Drive<br />
                    Suite 238<br />
                    Houston, Texas 77036
                  </address>
                </div>
              </div>
            </div>
          </aside>

          {/* Form column */}
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <form onSubmit={onSubmit} className="bg-slate-50 border border-slate-200 p-8 md:p-12" data-testid="contact-form">
                <div className="eyebrow text-emerald-600">Project brief</div>
                <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight leading-tight">
                  Tell us about what you're working on.
                </h2>

                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="Name *" htmlFor="name">
                    <input id="name" data-testid="contact-input-name" type="text" value={form.name} onChange={update("name")} required
                      className="w-full bg-white border border-slate-300 px-4 py-3 text-slate-900 focus:border-emerald-500 focus:ring-0 focus:outline-none transition-colors" />
                  </Field>
                  <Field label="Email *" htmlFor="email">
                    <input id="email" data-testid="contact-input-email" type="email" value={form.email} onChange={update("email")} required
                      className="w-full bg-white border border-slate-300 px-4 py-3 text-slate-900 focus:border-emerald-500 focus:ring-0 focus:outline-none transition-colors" />
                  </Field>
                  <Field label="Phone" htmlFor="phone">
                    <input id="phone" data-testid="contact-input-phone" type="tel" value={form.phone} onChange={update("phone")}
                      className="w-full bg-white border border-slate-300 px-4 py-3 text-slate-900 focus:border-emerald-500 focus:ring-0 focus:outline-none transition-colors" />
                  </Field>
                  <Field label="Company" htmlFor="company">
                    <input id="company" data-testid="contact-input-company" type="text" value={form.company} onChange={update("company")}
                      className="w-full bg-white border border-slate-300 px-4 py-3 text-slate-900 focus:border-emerald-500 focus:ring-0 focus:outline-none transition-colors" />
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="I'm interested in" htmlFor="interest">
                      <select id="interest" data-testid="contact-input-interest" value={form.interest} onChange={update("interest")}
                        className="w-full bg-white border border-slate-300 px-4 py-3 text-slate-900 focus:border-emerald-500 focus:ring-0 focus:outline-none transition-colors">
                        {INTERESTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </Field>
                  </div>
                  <div className="md:col-span-2">
                    <Field label="Message *" htmlFor="message">
                      <textarea id="message" data-testid="contact-input-message" rows={6} value={form.message} onChange={update("message")} required
                        placeholder="Share a few lines about your goals, timeline, and any specifics."
                        className="w-full bg-white border border-slate-300 px-4 py-3 text-slate-900 focus:border-emerald-500 focus:ring-0 focus:outline-none transition-colors resize-none" />
                    </Field>
                  </div>

                  {/* Honeypot — hidden from humans, crawled by bots */}
                  <div aria-hidden="true" className="hidden" style={{ position: "absolute", left: "-10000px", width: 1, height: 1, overflow: "hidden" }}>
                    <label htmlFor="website">
                      Website (leave blank)
                      <input
                        id="website"
                        name="website"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.website}
                        onChange={update("website")}
                      />
                    </label>
                  </div>
                </div>

                <div className="mt-10 flex flex-wrap items-center gap-6">
                  <button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={status === "loading"}
                    className="group inline-flex items-center gap-2 bg-slate-900 text-white px-8 py-4 font-medium hover:bg-emerald-500 hover:text-slate-950 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {status === "loading" ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                    ) : status === "success" ? (
                      <><CheckCircle2 className="w-4 h-4" /> Sent — we'll be in touch</>
                    ) : (
                      <>Send Message <Send className="w-4 h-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.75} /></>
                    )}
                  </button>
                  <div className="text-sm text-slate-500">We respond within one business day.</div>
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="border border-slate-200">
              <iframe
                title="Cutting Edge Technologies Headquarters Map"
                data-testid="contact-map"
                src={`https://www.google.com/maps?q=${CONTACT.mapQuery}&output=embed`}
                className="w-full h-[420px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="block text-xs font-medium text-slate-700 uppercase tracking-widest mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
